import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cx } from './cx';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** 'sheet': painel lateral no desktop e folha inferior no telemóvel. */
  variant?: 'modal' | 'sheet';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Diálogo acessível sobre o <dialog> nativo: foco preso, Esc fecha, fundo inerte.
 * ADR-020: primitivas da plataforma em vez de uma biblioteca headless.
 */
export function Dialog({ open, onClose, title, description, children, footer, variant = 'modal', size = 'md' }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const widths = { sm: 'sm:max-w-md', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl' };

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className={cx(
        'm-0 max-h-dvh max-w-none bg-transparent p-0 text-text-1 backdrop:bg-[rgb(13_14_16/0.45)] backdrop:backdrop-blur-[2px]',
        variant === 'sheet'
          ? 'fixed inset-x-0 bottom-0 top-auto w-full sm:inset-y-0 sm:left-auto sm:right-0 sm:h-dvh sm:w-[min(560px,100vw)]'
          : 'fixed inset-x-0 bottom-0 top-auto w-full sm:inset-0 sm:m-auto sm:h-fit',
        variant === 'modal' && widths[size],
      )}
    >
      {open && (
        <div
          className={cx(
            'flex max-h-[92dvh] flex-col bg-surface-1 shadow-2xl',
            variant === 'sheet' ? 'rounded-t-sheet sm:h-dvh sm:max-h-dvh sm:rounded-none' : 'rounded-t-sheet sm:rounded-[16px]',
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4 sm:px-6">
            <div>
              <h2 id={titleId} className="text-lg font-semibold tracking-tight">
                {title}
              </h2>
              {description && (
                <p id={descId} className="mt-1 text-sm text-text-2">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="-mr-2 inline-flex size-9 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface-2 hover:text-text-1"
            >
              <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
          {footer && <footer className="flex flex-wrap justify-end gap-3 border-t border-border px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}
